import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { auth } from '../config/firebase';

export function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'verifying' | 'ready' | 'success' | 'error'>('verifying');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const oobCode = new URLSearchParams(window.location.search).get('oobCode') || '';

  useEffect(() => {
    if (!oobCode) {
      setStatus('error');
      setError('Invalid or missing reset link. Please request a new one.');
      return;
    }
    verifyPasswordResetCode(auth, oobCode)
      .then((email) => { setEmail(email); setStatus('ready'); })
      .catch(() => {
        setStatus('error');
        setError('This reset link has expired or already been used. Please request a new one.');
      });
  }, [oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    setError('');
    setLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus('success');
    } catch {
      setError('Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: '#f3f4f6', padding: '24px',
    }}>
      <div style={{
        backgroundColor: '#ffffff', borderRadius: '16px', padding: '48px',
        width: '100%', maxWidth: '440px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: '0 0 8px 0' }}>
            Reset your password
          </h1>
          {status === 'ready' && (
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
              Setting new password for <strong>{email}</strong>
            </p>
          )}
        </div>

        {status === 'verifying' && (
          <p style={{ color: '#6b7280', fontSize: '15px' }}>Verifying reset link...</p>
        )}

        {status === 'error' && (
          <div style={{
            backgroundColor: '#fef2f2', border: '1px solid #fecaca',
            borderRadius: '8px', padding: '16px', color: '#b91c1c', fontSize: '14px',
          }}>
            {error}
            <div style={{ marginTop: '16px' }}>
              <a href="/" style={{ color: '#3b82f6', fontSize: '14px' }}>← Back to login</a>
            </div>
          </div>
        )}

        {status === 'success' && (
          <div>
            <div style={{
              backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0',
              borderRadius: '8px', padding: '16px', color: '#15803d',
              fontSize: '14px', marginBottom: '24px',
            }}>
              Password reset successfully! You can now sign in with your new password.
            </div>
            <a href="/" style={{
              display: 'block', textAlign: 'center', backgroundColor: '#3b82f6',
              color: '#ffffff', padding: '12px', borderRadius: '8px',
              textDecoration: 'none', fontWeight: '600', fontSize: '15px',
            }}>
              Go to Login
            </a>
          </div>
        )}

        {status === 'ready' && (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                New password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder="At least 8 characters"
                  required
                  style={{
                    width: '100%', padding: '10px 40px 10px 12px', fontSize: '15px',
                    border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '13px',
                  }}>
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Confirm new password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                placeholder="Repeat your password"
                required
                style={{
                  width: '100%', padding: '10px 12px', fontSize: '15px',
                  border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {error && (
              <p style={{ color: '#b91c1c', fontSize: '13px', marginBottom: '16px' }}>{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '12px', backgroundColor: loading ? '#93c5fd' : '#3b82f6',
                color: '#ffffff', border: 'none', borderRadius: '8px',
                fontSize: '15px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
